// Three.js-free mascot catalog: outfit calibration, moods, reactions and saved
// preference helpers. Safe to import from app-wide code without loading 3D.

// handOffsetY values compensate for slanted or flared sleeve caps whose
// vertex medians differ from the area-weighted centre of the real opening.
export const OUTFITS = [
  {
    // Original: Acey as designed, the lightbulb body with no outfit. The
    // default look; learners pick an outfit later.
    // `plain`: Acey as designed, intentionally without an outfit or props.
    id: 'original', number: '01', label: 'Original', type: 'glb', plain: true,
    url: '/mascot/poses/original.glb', unitScale: 1,
    fullCharacter: true, fullCharacterOffsetY: 0,
    brainMaterials: ['Material.002'],
    // The designer body with no outfit (tmp/build-original.mjs): one hand
    // raised in a hello: both hands up beside the face, waving together.
    hold: {
      right: { target: [-0.348, 0.449, 0.065], pole: [1, -0.6, 0.3], roll: -90 },
      left: { target: [0.348, 0.449, 0.065], pole: [1, -0.6, 0.3], roll: -90 },
      // Round elbows on the slender arm tubes.
      frame: { elbowBlend: [0.33, 0.48] },
      // Short arms as in the approved design render (client, 2026-09-18):
      // the tube between the shoulder and the wrist is half as long.
      armLength: { from: 0.2, to: 0.45, scale: 0.5 },
      wave: { side: 'both', degrees: 22, speed: 9 },
    },
  },
  {
    id: 'classic', number: '02', label: 'Engineer', type: 'glb', url: '/mascot/outfits/classic.glb', unitScale: 100,
    modelOffsetY: 11.65,
    // The supplied sleeve root already sits 0.6 units inside the torso.
    // Moving its pivot farther inward makes the resting rotation pull the
    // visible shoulder edge away from the body, so preserve its authored pivot.
    shoulderOverlap: 0,
    // As supplied, the hard hat sits down over the eyes (brim at y=63-68,
    // eyes at y=62-77) and the glasses rims cut through its brim, while the
    // thick scarf crowds the chin. Tip the hat back 14° and lift it so the
    // front brim clears the glasses, and slim the scarf a little.
    gearAdjust: {
      materialName: 'non_quanao_1001',
      hat: { fromY: 55, center: [0, 63, 0], scale: 0.92, pivot: [0, 63, -20], tiltDeg: 14, liftY: 5 },
      collar: { fromY: 31, toY: 50, blendY: 36, shrink: 0.9, dropY: 1.5 },
    },
    // With the hat tipped back, a fringe of brain curls below y=84 shows
    // under the brim; higher curls would bulge past it at the sides.
    headwearHairCutoffY: 84,
    // The short utility shirt ends above the exposed base arm. A tapered
    // fabric shroud follows the arm pivot and overlaps both pieces, keeping
    // the rear shoulder as a sleeve transition rather than a bare tube.
    shoulderShroud: {
      materialName: 'ao_trong_1001', innerX: 8, outerX: 27,
      shoulderRadius: 6.6, cuffRadius: 5.6,
    },
    showBaseArms: true, armPose: { shoulderX: 19, shoulderY: 18.6, outerMin: 24 },
  },
  {
    id: 'doctor', number: '03', label: 'Healthcare', type: 'glb', url: '/mascot/outfits/doctor.glb', unitScale: 100,
    modelOffsetY: 18.84,
    shoulderOverlap: 1.6,
    outfitTint: '#c5e7e1',
    cuffOverlap: 3.4, armPose: { shoulderX: 19, shoulderY: 18.8, outerMin: 45 },
  },
  {
    id: 'long-vest', number: '04', label: 'Scientist', type: 'glb', url: '/mascot/outfits/long-vest.glb', unitScale: 100,
    modelOffsetY: 18.84,
    // Each pink sleeve is a separate supplied mesh whose inner edge begins
    // exactly at x=19.3. Keep the rotation centre on that edge: an inset
    // pivots the edge down and exposes the body at a three-quarter view.
    shoulderOverlap: 0,
    outfitTint: '#c8d0f1',
    cuffOverlap: 3.4, handOffsetY: -2.24,
    armPose: { shoulderX: 19.3, shoulderY: 18.3, outerMin: 43 },
  },
  {
    id: 'vest', number: '05', label: 'Business', type: 'glb', url: '/mascot/outfits/vest.glb', unitScale: 100,
    // The supplied vest ends well below the mascot's chin. Lift the complete
    // garment and its authored shoulder pivots together so the collar, cuffs,
    // hands and action poses remain one connected character.
    modelOffsetY: 17,
    shoulderOverlap: 1.6,
    // Each sleeve is a separate tube (radius 6.5, vertices only at its ends)
    // whose open inner end showed as a pale rim behind the shoulder once the
    // arm swings forward to hold a prop. A short tube of the suit cloth, a
    // touch wider than the sleeve, starts inside the jacket and covers it.
    outfitArmShroud: {
      materialName: 'openPBR_shader1', color: '#0c2c64', innerX: 12.5, outerX: 30,
      shoulderRadius: 7.1, cuffRadius: 6.7,
    },
    cuffOverlap: 3.2, armPose: { shoulderX: 18.3, shoulderY: 18.6, outerMin: 45 },
  },
  {
    id: 'artist', number: '06', label: 'Creative', type: 'glb', url: '/mascot/outfits/artist.glb', unitScale: 100,
    modelOffsetY: 17.66,
    // The short-sleeve shoulder has to be cut at its authored outside edge,
    // while its rotation centre stays under the bib. Decoupling those two
    // positions gives the sleeve a real underlap instead of a visible seam
    // at side angles.
    shoulderPivotInset: 1.4,
    shoulderCutOverlap: 0,
    // The beret's opening shows the base hair as pale patches in its inner
    // cavity. The supplied beret also has an open crown, so crop is not
    // sufficient at a top-down camera angle: keep the base hair fully hidden
    // while this fitted hat is selected.
    headwearHairCutoffY: 70,
    hideBaseHair: true,
    // The base body shell extends through the open crown of the supplied
    // beret. Crop only its crown-facing triangles for this outfit; other
    // outfits keep their complete base body.
    headwearBodyCutoffY: 70,
    // The supplied beret includes a disconnected, raised crown fragment that
    // reads as an unintended bump under the preview lights. Its topology is
    // verified below before it is removed; the guard fails closed if 3D sends
    // an updated artist asset.
    removeBeretCrownNub: true,
    // Match the supplied cream artist shirt at the exposed upper arm. This
    // is a real garment-material sleeve cap, not a skin-coloured patch.
    shoulderShroud: {
      materialName: 'lambert14_1001', innerX: 8, outerX: 25,
      shoulderRadius: 6.3, cuffRadius: 5.5,
    },
    showBaseArms: true, armPose: { shoulderX: 19, shoulderY: 18.55, outerMin: 24 },
  },
  {
    // Singer: the 3D team's body_rig_AO_casi2 character (pink blazer,
    // headphones, microphone). It has no separate garment, so it is a complete
    // character whose props are always on.
    id: 'singer', number: '07', label: 'Singer', type: 'glb', url: '/mascot/poses/singer.glb', unitScale: 1,
    fullCharacter: true, fullCharacterOffsetY: 0,
    accessories: { label: 'Headphones & microphone', builtIn: true },
    brainMaterials: ['Material.004.1001'],
    // The microphone sits in a fist held up by the mouth; the other arm rests.
    hold: {
      left: {
        target: [0.12, 0.39, 0.28],
        pole: [1, -1, -0.4],
        grip: { meshes: ['Prongs_low_Microphone_0'], at: 0.3 },
      },
      right: { target: [-0.34, 0.13, 0.08], pole: [1, 0.2, -1] },
      // A softer elbow: the sharp bend folded the blazer sleeve into a crease
      // that caught the light as a pale line (client, 2026-09-19).
      frame: { elbowBlend: [0.33, 0.48] },
    },
  },
  {
    // Fashion (AO_TT, "thời trang"): lavender top, navy overalls, handbag.
    id: 'activewear', number: '08', label: 'Fashion', type: 'glb', url: '/mascot/outfits/activewear.glb', unitScale: 100,
    modelOffsetY: 16.14,
    // Its continuous top is split into a moving sleeve and fixed torso at
    // runtime. Keep that cut at the asset's outer shoulder edge, but tuck the
    // rotation centre 1.6 units inside the torso. The moving sleeve then stays
    // beneath the torso through the rest pose and all three-quarter rotations.
    shoulderPivotInset: 1.6,
    shoulderCutOverlap: 0,
    // The top is a connected mesh while the dark bib stays fixed. A
    // moving, open sleeve shroud uses the same source cloth to bridge their
    // joint without leaving a U-shaped socket at the rear shoulder.
    outfitArmShroud: {
      materialName: 'ao_trong', innerX: 13.5, outerX: 31,
      shoulderRadius: 6.2, cuffRadius: 5.55,
    },
    cuffOverlap: 3.2, armPose: { shoulderX: 19, shoulderY: 18.55, outerMin: 45 },
  },
  {
    id: 'graduation', number: '09', label: 'Scholar', type: 'fbx',
    url: '/mascot/outfits/graduation/graduation.fbx', unitScale: 1,
    modelOffsetY: 17,
    shoulderOverlap: 1.6,
    // The cap sits on top of the whole brain, as in the client's Scholar art.
    // Cropping the brain to a fringe under the board (cutoff 96) left a thin
    // ring of bubbles around a bare head from behind (client, 2026-09-27).
    // The board (y 102-108 as supplied), button and tassel rise 10, and the
    // brain is cut only inside the board, where nothing can poke through.
    capLift: {
      fromY: 100, liftY: 10, skullBelowY: 100, skullScale: 0.6,
      // The tassel collar and tuft are separate mesh islands below the cord.
      attached: { min: [-47, 55, 0], max: [-38, 76, 10] },
      tasselCord: { min: [-45, 70, -3], max: [-4, 109, 10] },
      // The full brain otherwise hides the tassel from the side and rear.
      tasselOutsetX: -9,
    },
    headwearHairCutoffY: 113,
    // The supplied gown, cap and tassel share one plain white material. Paint
    // them as in the approved Scholar render: a black gown and cap with a gold
    // tassel (cord and tuft hang at x≈-43, its button tops the cap).
    // The gown is three nested torso layers; the inner two get their own
    // colors so the vest reads as a separate layer under the open gown.
    regionColors: {
      color: '#26262e',
      regions: [
        { color: '#d8a31f', min: [-47, 38, 0.5], max: [-38.5, 60, 9.5] },
        { color: '#d8a31f', min: [-43.5, 88.5, 2.5], max: [-39.2, 93, 6.8] },
      ],
      components: [
        // The hanging tassel is three disconnected pieces. Color the entire
        // collar and tuft, not only the few vertices inside the narrow bands.
        { color: '#d8a31f', min: [-47, 38, 0], max: [-38, 61, 10] },
        // Tie: the narrow strip down the front.
        { color: '#d8a31f', min: [-4.2, 6.5, 15.5], max: [4.2, 21, 18.5] },
        // Buttons down the vest front.
        { color: '#b98a1c', min: [-0.6, -2.5, 18], max: [0.6, 13, 19.4] },
        // Shirt: the innermost torso layer.
        { color: '#f1f0f6', min: [-22.6, -4.8, -17.8], max: [22.6, 25.4, 17.8] },
        // Vest: the middle layer, between shirt and gown.
        { color: '#555b6b', min: [-23.8, -5.6, -18.8], max: [23.8, 26, 20.3] },
      ],
    },
    // The sleeve tapers to a narrow cap where it enters the gown, which
    // showed as a pale seam at the back of the shoulder; a short tube of the
    // gown cloth (radius 6, the sleeve's own is 5.3-5.9) fills that root.
    // regionColors paints it black with the rest of the gown.
    outfitArmShroud: {
      materialName: 'openPBR_shader1', innerX: 16, outerX: 31,
      shoulderRadius: 6.1, cuffRadius: 6.0,
    },
    cuffOverlap: 3.4, handOffsetY: 3.49,
    armPose: { shoulderX: 21.1, shoulderY: 18.8, outerMin: 45 },
  },
  {
    id: 'cloak', number: '10', label: 'Cozy', type: 'glb', url: '/mascot/outfits/cloak.glb', unitScale: 100,
    // Measured hood peak is 102.46 versus the base-hair peak 116.40.
    // This 15.44 lift supplies a 1.5-unit safety clearance at the crown.
    modelOffsetY: 15.44, integratedHood: 'liftAll',
    shoulderOverlap: 1.4,
    // The hood and coat are one connected mesh. Its inner opening reaches
    // y=70 after the calibrated lift, so no base-brain triangle can show
    // through the hood cavity.
    headwearHairCutoffY: 70,
    outfitTint: '#e8b5d3',
    cuffOverlap: 3.2, armPose: { shoulderX: 16.3, shoulderY: 19.45, outerMin: 45 },
  },
  {
    id: 'hoodie', number: '11', label: 'Gamer', type: 'glb', url: '/mascot/outfits/hoodie.glb', unitScale: 100,
    modelOffsetY: 15.72,
    shoulderOverlap: 1.6,
    outfitTint: '#c8b7e8',
    cuffOverlap: 3.4, armPose: { shoulderX: 16.1, shoulderY: 19.45, outerMin: 45 },
  },
  {
    id: 'wizard', number: '12', label: 'Fantasy', type: 'glb', url: '/mascot/outfits/wizard.glb', unitScale: 100,
    modelOffsetY: 17.12,
    shoulderOverlap: 1.6,
    // The witch-hat brim ends at y=75.1. Keep only the fringe below it so
    // the crown cannot emerge through the pointed hat from any camera angle.
    headwearHairCutoffY: 74.5,
    // The source wizard material is already very dark; blend toward violet
    // instead of multiplying it to preserve a rich, readable fantasy tone.
    outfitTint: '#a25bdf', outfitTintBlend: 0.42,
    cuffOverlap: 3.4, handOffsetY: 5.03,
    armPose: { shoulderX: 15.3, shoulderY: 15.25, outerMin: 45 },
  },
];

// Store pivots in the original asset coordinates, then apply the same lift
// to the clothing and both hand rigs. A rig's split line and its rotation
// centre are normally identical, but a connected garment needs the centre
// tucked inside the torso while keeping the split at the supplied sleeve edge.
// That intentional underlap prevents a background slit at three-quarter views.
export const getOutfitArmPose = (outfit) => {
  const shoulderPivotInset = outfit.shoulderPivotInset ?? outfit.shoulderOverlap ?? 0;
  const shoulderCutOverlap = outfit.shoulderCutOverlap ?? shoulderPivotInset;
  return {
    ...outfit.armPose,
    shoulderX: outfit.armPose.shoulderX - shoulderPivotInset,
    sleeveCutX: outfit.armPose.shoulderX - shoulderCutOverlap,
    shoulderY: outfit.armPose.shoulderY + (outfit.modelOffsetY || 0),
  };
};

// A mood sets how Acey moves while idle on every page (see mascotMotion.js).
export const MOODS = [
  { id: 'ready', label: 'Ready', icon: '🙂', description: 'Calm and steady' },
  { id: 'curious', label: 'Curious', icon: '🤔', description: 'Tilts and looks around' },
  { id: 'cheerful', label: 'Cheerful', icon: '😄', description: 'Bouncy and upbeat' },
];

// The reactions Acey plays by itself while the learner studies. Ids match
// ACEY_REACTIONS in components/acey/aceyBrain.js; `when` describes the real
// trigger so the customizer preview explains what each one is for.
export const REACTIONS = [
  {
    id: 'hello', label: 'Hello', icon: '👋', duration: 3,
    when: 'Greets you on the Dashboard and in AI Tutor',
    message: 'waves hello.',
  },
  {
    id: 'think', label: 'Think', icon: '💭', duration: 2.8,
    when: 'When you upload a problem or ask for a hint',
    message: 'is thinking it through with you.',
  },
  {
    id: 'focus', label: 'Focus', icon: '🎯', duration: 2.35,
    when: 'When a solution or study session starts',
    message: 'is settling in to focus.',
  },
  {
    id: 'celebrate', label: 'Celebrate', icon: '🎉', duration: 2.65,
    when: 'When you finish a problem or save a note',
    message: 'is celebrating your progress.',
  },
  {
    id: 'encourage', label: 'Encourage', icon: '💪', duration: 2.4,
    when: 'When something goes wrong, so you keep going',
    message: 'is cheering you on.',
  },
  {
    id: 'rest', label: 'Break', icon: '☕', duration: 3.4,
    when: 'After a long study session',
    message: 'stretches and suggests a short break.',
  },
];

export const REACTION_DURATIONS = Object.fromEntries(
  REACTIONS.map((reaction) => [reaction.id, reaction.duration])
);

// Brain colors a learner can pick. `swatch` is the UI dot; `tint` becomes the
// color of the textured brain material in 3D (null keeps the designer's
// lavender). Technician's brain is baked into its single texture and stays
// under the hard hat, so it has no brainMaterials.
export const COLORS = [
  // The approved lightbulb render: a soft pink, sky-blue and violet blend
  // across the brain rather than one color (`blend`, see applyBrainTint).
  {
    id: 'dreamy',
    label: 'Dreamy',
    swatch: 'linear-gradient(135deg, #f7a8d8 0%, #b69cff 52%, #8fc1ff 100%)',
    glow: '#b69cff',
    tint: null,
    blend: ['#ffc0e1', '#b9d3ff', '#dac4ff'],
  },
  { id: 'lavender', label: 'Lavender', swatch: '#b69cff', tint: null },
  { id: 'bubblegum', label: 'Bubblegum', swatch: '#f58dc8', tint: '#ff9fd3' },
  { id: 'peach', label: 'Peach', swatch: '#ff9d5c', tint: '#ffb68a' },
  { id: 'sunny', label: 'Sunny', swatch: '#ffcf4a', tint: '#ffdb6e' },
  { id: 'mint', label: 'Mint', swatch: '#4fcf93', tint: '#8feabb' },
  { id: 'sky', label: 'Sky', swatch: '#62c8f2', tint: '#98dcff' },
  { id: 'ocean', label: 'Ocean', swatch: '#4f82f4', tint: '#8aa6ff' },
  { id: 'grape', label: 'Grape', swatch: '#8b5cf6', tint: '#a88bff' },
];

export const getBrainTint = (colorId) => COLORS.find((color) => color.id === colorId)?.tint || null;
export const getBrainBlend = (colorId) => COLORS.find((color) => color.id === colorId)?.blend || null;

export const DEFAULT_OUTFIT_ID = 'original';
export const DEFAULT_MOOD_ID = 'ready';
// The approved Acey look: the soft pink, sky-blue and violet brain.
export const DEFAULT_COLOR_ID = 'dreamy';
export const DEFAULT_BUDDY_NAME = 'Acey';
export const BUDDY_NAME_MAX_LENGTH = 20;

// Mirrors the backend rule: letters (any language), digits, spaces and . ' -
// up to 20 characters. Anything else falls back to the default name ('').
export const normalizeBuddyName = (value) => {
  if (typeof value !== 'string') return '';
  const name = value.trim().replace(/\s+/g, ' ');
  // For a while the buddy was called "ACE"; that saved name is the default now.
  if (/^ace$/i.test(name)) return '';
  return /^[\p{L}\p{N}][\p{L}\p{N} .'-]{0,19}$/u.test(name) ? name : '';
};

export const getBuddyName = (appearance) => normalizeBuddyName(appearance?.name) || DEFAULT_BUDDY_NAME;
export const PREFERENCES_KEY = 'ace-mascot-preferences-v1';

// Designer poses with props from the `pose_fbx` delivery. Each is a complete
// character that replaces the layered garment while accessories are switched
// on. The scene fits full characters to the shared base body at runtime, so
// no per-file scale calibration is needed here.
//
// The original `pose_fbx` files were built on the Technician body (work
// overalls and tool belt) without outfit textures, so roles are added back
// one at a time from the 3D team's corrected GLB re-exports, each compared
// side by side with its garment in outfits/. See public/mascot/README.md.
export const POSED_VARIANTS = {
  // Engineer: the 3D team's posed worker (mascot_Rig_CN2, formerly shown as
  // a separate Technician) holding a wrench and power drill, recolored to the
  // approved Engineer design — yellow hard hat, brown tool belt and boots, and
  // the pearl face — by tmp/build-design-mascots.mjs.
  classic: {
    url: '/mascot/poses/engineer.glb',
    label: 'Wrench & power drill',
    brainMaterials: ['engineer_brain'],
    // The wide hard hat makes a whole-body Curious lean look off balance.
    idleTiltScale: 0.2,
  },
  // Healthcare: body_AO_BS2.glb — white lab coat over mint scrubs with the
  // stethoscope around the neck (kept still). The fist holds the clipboard by
  // its outer edge in front of the body, paper toward the viewer (client,
  // 2026-09-19). Coat sleeves recolored by tmp/build-healthcare-sleeves.mjs.
  doctor: {
    url: '/mascot/poses/healthcare.glb',
    label: 'Stethoscope & clipboard',
    brainMaterials: ['Material.006'],
    hold: {
      left: {
        target: [0.33, 0.3, 0.26],
        pole: [1, -1, -0.5],
        grip: { meshes: ['Cube002'], at: 0.35, shift: 0.075, spin: 140 },
      },
      right: { target: [-0.34, 0.13, 0.08], pole: [1, 0.2, -1] },
      fixed: ['Object_4', 'Object_5'],
    },
  },
  // Fashion: body_AO_TT2.glb (lavender top, navy overalls) carrying the purple
  // tote from the approved render (tmp/build-fashion-bag.mjs). The fist closes
  // round the top of the handle and the bag hangs straight in front of the hip
  // (an `upright` grip); the free arm hangs relaxed. Before, the palm only lay
  // flat on the handle and the free hand was bent to the hip with a sharp
  // elbow (client, 2026-09-27).
  activewear: {
    url: '/mascot/poses/fashion.glb',
    label: 'Handbag',
    // Split out of the shared skin mesh by tmp/split-posed-brains.mjs.
    brainMaterials: ['fashion_brain'],
    hold: {
      left: {
        target: [0.34, 0.28, 0.28],
        pole: [1, -1, -0.5],
        // Turns the fist so its curl runs along the handle.
        wristRoll: 25,
        // `handle` is the top of the handle (TOTE.grip in build-fashion-bag).
        grip: { meshes: ['fashion_tote'], upright: true, handle: [0.37, 0.25, 0.09] },
      },
      right: { target: [-0.46, 0.1, 0.14], pole: [1, -1, -0.6], wristRoll: -40 },
      frame: { elbowBlend: [0.33, 0.48] },
    },
  },
  // Creative: BODY_AO_HoaSi2.glb — brown beret sitting on the brain, white
  // shirt, blue overalls; a paintbrush in one fist and the palette resting on
  // the other, open palm (laid flat by tmp/build-artist-palette.mjs; the hand
  // turns palm-up so no finger cuts through it, tipped 40° past flat so the
  // paint faces the viewer).
  // Both hands turn at the wrist: rolling the whole forearm twisted the
  // elbows thin, and the brush elbow bent sharply at the hip against the
  // overalls (client, 2026-09-27). The brush elbow now sits out beside the
  // body, elbows bend round, and the glassy arms are drawn solid.
  artist: {
    url: '/mascot/poses/artist.glb',
    label: 'Paintbrush & palette',
    brainMaterials: ['Material.008.1001'],
    hold: {
      right: {
        target: [-0.33, 0.31, 0.29],
        pole: [1, -0.6, -0.6],
        wristRoll: -90,
        grip: { meshes: ['Cylinder', 'Cylinder001', 'Cylinder002'], at: 0.3 },
      },
      // -140° is the palm-up turn the forearm used to make as 220°.
      left: { target: [0.46, 0.27, 0.14], pole: [0.3, -1, -1], wristRoll: -140 },
      props: { left: ['Cylinder003'] },
      frame: { elbowBlend: [0.33, 0.48] },
      opaqueArms: true,
    },
  },
  // Cozy: body_AoKhoacTrumDau2.glb — pink hooded jacket with the brain
  // showing inside the hood; both hands cup the mug.
  cloak: {
    url: '/mascot/poses/hooded-jacket.glb',
    label: 'Warm mug',
    brainMaterials: ['cozy_brain'],
    hold: {
      right: { target: [-0.1, 0.31, 0.3], pole: [1, -1, -0.5] },
      left: { target: [0.07, 0.31, 0.3], pole: [1, -1, -0.5] },
    },
  },
  // Fantasy: phu_thuy.glb — wizard hat, robe and belt.
  wizard: {
    url: '/mascot/poses/wizard.glb',
    label: 'Magic wand & spell book',
    brainMaterials: ['toc'],
    materialColors: { 'Material.001': '#eeb0d4' },
    // The wand hand hangs relaxed at the side, holding the wand like a staff;
    // the other arm reaches almost straight forward with the hand turned
    // palm-up at the wrist under the open spell book (client, 2026-09-24:
    // a bent elbow folded the wide bell sleeve into a sharp crease, and the
    // raised wand fist sat outside its sleeve).
    hold: {
      right: { target: [-0.42, 0.2, 0.18], pole: [1, -1, -0.5] },
      left: { target: [0.28, 0.27, 0.41], pole: [1, -1, -0.4], wristRoll: 180 },
      props: { right: ['pCylinder3'] },
      // The open spell book (rebuilt by tmp/build-wizard-book.mjs) stays still
      // in front of the body, raised to meet the straighter arm. The belt
      // stays on the robe instead of being tugged by the arms.
      fixed: ['spell_book', 'polySurface1002'],
      shift: { spell_book: [0, 0.035, 0.03] },
      // The witch hat (robe, hat band and buckle materials above the neck)
      // rides higher so the brain shows under its brim.
      headwear: { materials: ['QUAN_AO', 'That_lung', 'pk_non'], fromY: 0.9, lift: 0.12 },
      frame: {
        // The book-side bell sleeve rises to y≈0.48, above the default arm
        // band; without this its top stayed put and tore into a flap.
        sleeveTop: { fromX: 0.4, maxY: 0.52 },
        // A long, soft shoulder blend so the wide sleeve turns forward
        // without a pinched point at the armpit; the robe at belt height
        // (below armpitY) stays on the body.
        shoulderBlend: [0.1, 0.34],
        torsoX: [0.3, 0.36],
        armpitY: [0.3, 0.36],
      },
      // The shared body material is see-through: opaque hands keep the wrists
      // solid and hide the wand inside the fist.
      opaqueArms: true,
    },
  },
  // body_AO_hoodi2.glb (2026-09-14): purple ribbed hoodie and shoes match
  // outfits/hoodie.glb.
  hoodie: {
    url: '/mascot/poses/hoodie.glb',
    label: 'Headphones & game controller',
    brainMaterials: ['Material.002'],
    // Both hands come in to hold the game controller in front of the chest.
    hold: {
      right: { target: [-0.1, 0.33, 0.27], pole: [1, -1, -0.5] },
      left: { target: [0.1, 0.33, 0.27], pole: [1, -1, -0.5] },
    },
  },
};

// Props taken from the 3D team's deliveries (or built where a delivered mesh
// is unusable) and held in Acey's fists on the role's approved garment, for
// roles whose posed delivery wears the wrong clothes. Built by
// tmp/extract-props.mjs; see public/mascot/README.md.
// `holdArms` lists the arms that hold something and rest lifted.
export const PROP_SETS = {
  // The flask in one fist; a microscope stands on the floor by the other
  // hand, as in the approved Scientist render (client, 2026-09-19).
  'long-vest': { url: '/mascot/props/long-vest.glb', label: 'Science flask & microscope', holdArms: ['left'] },
  // The mug arm comes up in front of the chest (client, 2026-09-19); the
  // briefcase hangs from the other hand.
  vest: {
    url: '/mascot/props/vest.glb',
    label: 'Briefcase & coffee mug',
    holdArms: ['left', 'right'],
    holdPose: { left: { down: 16, forward: 72, bend: 40 } },
  },
  // Bend the pencil arm toward the body while the other hugs the books.
  graduation: {
    url: '/mascot/props/graduation.glb',
    label: 'Pencil & books',
    holdArms: ['right', 'left'],
    holdPose: { right: { down: 32, forward: 50, bend: 38, tilt: 10 }, left: { down: 38, forward: 80 } },
  },
};

export const getOutfitAccessories = (outfit) => {
  if (!outfit) return null;
  if (outfit.accessories) return outfit.accessories;
  const set = POSED_VARIANTS[outfit.id] || PROP_SETS[outfit.id];
  return set ? { label: set.label, builtIn: false } : null;
};

// Returns the model that should be displayed for an outfit: a matching posed
// character, the approved garment holding its props, or the plain garment
// when accessories are switched off.
export const resolveOutfitVariant = (outfit, accessoriesEnabled = true) => {
  const posed = outfit && POSED_VARIANTS[outfit.id];
  const propSet = outfit && PROP_SETS[outfit.id];
  if (!accessoriesEnabled || (!posed && !propSet)) return outfit;
  if (!posed) return { ...outfit, props: propSet, variant: 'props' };
  return {
    ...outfit,
    type: 'glb',
    url: posed.url,
    unitScale: 1,
    fullCharacter: true,
    fullCharacterOffsetY: 0,
    // Garment lifts do not apply to a complete character; its feet share the
    // base body's floor.
    modelOffsetY: 0,
    brainMaterials: posed.brainMaterials || [],
    hold: posed.hold,
    materialColors: posed.materialColors,
    variant: 'posed',
  };
};

export const readPreferences = () => {
  if (typeof window === 'undefined') return {};

  try {
    const preferences = JSON.parse(window.localStorage.getItem(PREFERENCES_KEY) || '{}');
    return preferences && typeof preferences === 'object' ? preferences : {};
  } catch (error) {
    return {};
  }
};

export const isKnownPreference = (items, id) => items.some((item) => item.id === id);
